import Plot from 'features/plotly'
import { Layout, PlotData } from 'plotly.js'
import { ComponentProps, createElement, FC, useMemo } from 'react'
import styled from 'styled-components'

export const Title = styled.h1`
  font-size: 1.25rem;
  margin: 0 0 0.25rem;
`

export const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.grayscale['400']};
  margin: 0 0 1rem;
`

export const Section = styled.section`
  margin: 1.25rem 0;
`

export const SectionTitle = styled.h2`
  font-size: 0.9375rem;
  margin: 0 0 0.625rem;
  color: ${({ theme }) => theme.colors.grayscale['200']};
`

export const ErrorText = styled.p`
  color: ${({ theme }) => theme.colors.error['400_base']};
  margin: 0.5rem 0;
  white-space: pre-wrap;
`

export const NativeSelect = styled.select`
  padding: 0.3rem 0.5rem;
  border: 1px solid ${({ theme }) => theme.colors.grayscale['600']};
  border-radius: 0.375rem;
  background: transparent;
  color: ${({ theme }) => theme.colors.grayscale['100']};
  margin-bottom: 0.5rem;
`

/**
 * Hard height box around Plotly. Do NOT use styled(Plot) alone — the lazy
 * Suspense wrapper + flex parents let charts collapse to a title strip.
 */
const PlotFrame = styled.div<{ $heightRem: number }>`
  display: block;
  width: 100%;
  height: ${({ $heightRem }) => $heightRem}rem;
  min-height: ${({ $heightRem }) => $heightRem}rem;
  max-height: ${({ $heightRem }) => $heightRem}rem;
  flex: 0 0 ${({ $heightRem }) => $heightRem}rem;
  position: relative;
  overflow: hidden;

  /* Force nested Plotly containers to fill the frame */
  & .js-plotly-plot,
  & .plot-container,
  & .svg-container {
    width: 100% !important;
    height: 100% !important;
  }
`

type PlotProps = ComponentProps<typeof Plot>

export type SizedPlotProps = Omit<PlotProps, 'style' | 'layout'> & {
  $heightRem: number
  layout?: Partial<Layout>
  data: Partial<PlotData>[]
}

export const SizedPlot: FC<SizedPlotProps> = ({
  $heightRem,
  layout,
  data,
  config,
  useResizeHandler = true,
  ...rest
}) => {
  const heightPx = Math.round($heightRem * 16)
  const mergedLayout = useMemo(
    () => ({
      autosize: true,
      ...layout,
      // Explicit height beats flex/percentage collapse inside Plotly.
      height: heightPx,
      width: undefined,
    }),
    [layout, heightPx]
  )

  return createElement(
    PlotFrame,
    { $heightRem },
    createElement(Plot, {
      data,
      layout: mergedLayout,
      useResizeHandler,
      config: { displayModeBar: false, responsive: true, ...config },
      style: { width: '100%', height: '100%' },
      ...rest,
    })
  )
}
